import { FormEvent, useState } from "react";
import {
  HiOutlineEnvelope,
  HiOutlineMapPin,
  HiOutlinePhone,
  HiOutlineArrowUpRight,
} from "../../components/common/LightIcons";
import PageHero from "../../components/common/PageHero";
import Reveal from "../../components/common/Reveal";
import Seo from "../../components/common/Seo";
import { services } from "../../data/siteData";

type SubmitStatus = "success" | "error" | null;

const COMPANY_EMAIL = "info@navasthainfotech.com";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setStatus(null);
    setErrorMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "Unable to send your enquiry.");
      }

      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
        website: "",
      });
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to send your enquiry right now.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Us"
        description="Start a software development project with Navastha Infotech. Contact our Jaipur team for web, mobile, cloud, AI, e-commerce or dedicated engineering services."
      />
      <PageHero
        eyebrow="Let's talk"
        title={
          <>
            Tell us what you're ready to{" "}
            <span className="gradient-text">build next.</span>
          </>
        }
        text="Share the challenge, the idea or the roadmap. We’ll help you identify the right next step."
      />

      <section className="section-pad contact-page">
        <div className="shell contact-layout">
          <Reveal>
            <div className="contact-info">
              <span className="eyebrow">
                <i /> Contact details
              </span>
              <h2>Start with a conversation.</h2>
              <p>
                Whether you need a new product, modernization, specialist
                engineering or a dedicated team, send us the context and we’ll
                get the discussion moving.
              </p>

              <div className="contact-cards">
                <a href={`mailto:${COMPANY_EMAIL}`}>
                  <span>
                    <HiOutlineEnvelope />
                  </span>
                  <div>
                    <small>Email</small>
                    <strong>{COMPANY_EMAIL}</strong>
                  </div>
                  <HiOutlineArrowUpRight />
                </a>

                <a href="tel:+919929572110">
                  <span>
                    <HiOutlinePhone />
                  </span>
                  <div>
                    <small>Phone</small>
                    <strong>+91 99295 72110</strong>
                  </div>
                  <HiOutlineArrowUpRight />
                </a>

                <div>
                  <span>
                    <HiOutlineMapPin />
                  </span>
                  <div>
                    <small>Office</small>
                    <strong>Vaishali Nagar, Jaipur, Rajasthan 302034</strong>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form className="contact-form-modern" onSubmit={submit}>
              <div className="form-head">
                <span>Project enquiry</span>
                <h2>How can we help?</h2>
              </div>

              <div className="form-grid">
                <label>
                  <span>Your name *</span>
                  <input
                    required
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                  />
                </label>

                <label>
                  <span>Work email *</span>
                  <input
                    required
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com"
                  />
                </label>

                <label>
                  <span>Phone</span>
                  <input
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 ..."
                  />
                </label>

                <label>
                  <span>Service</span>
                  <select
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.slug} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="full">
                  <span>Tell us about the project *</span>
                  <textarea
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="What are you building, improving or trying to solve?"
                    rows={6}
                  />
                </label>

                <label className="contact-honeypot" aria-hidden="true">
                  <span>Website</span>
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) =>
                      setForm({ ...form, website: e.target.value })
                    }
                  />
                </label>
              </div>

              <button
                className="btn btn-primary btn-lg"
                type="submit"
                disabled={loading}
              >
                {loading ? "Sending enquiry..." : "Send enquiry"}
                {!loading && <span>↗</span>}
              </button>

              {status === "success" && (
                <div className="form-status form-success" role="status">
                  ✓ Thank you! Your enquiry has been sent successfully. Our team
                  will get back to you shortly.
                </div>
              )}

              {status === "error" && (
                <div className="form-status form-error" role="alert">
                  {errorMessage ||
                    `We could not send your enquiry. Please email us at ${COMPANY_EMAIL}.`}
                </div>
              )}

              <small className="form-note">
                Your enquiry is sent securely through Navastha Infotech's
                Hostinger mailbox.
              </small>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
